package pl.mothdigital.neuronowi.components.layouts

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.*
import kotlinx.browser.*
import org.jetbrains.compose.web.css.*
import pl.mothdigital.neuronowi.common.resources.StringResources
import pl.mothdigital.neuronowi.common.resources.UrlResources
import pl.mothdigital.neuronowi.components.sections.footer.*
import pl.mothdigital.neuronowi.components.sections.navheader.*
import pl.mothdigital.neuronowi.components.widgets.*
import pl.mothdigital.neuronowi.theme.*

private fun org.w3c.dom.Document.updateHeadTag(
    selector: String,
    tagName: String,
    attributes: Map<String, String>
) {
    val element = head?.querySelector(selector)
        ?: createElement(tagName).also { head?.appendChild(it) }

    attributes.forEach { (key, value) ->
        element.setAttribute(key, value)
    }
}

@Composable
fun PageLayout(
    title: String,
    description: String? = null,
    ogImage: String? = null,
    backgroundColor: CSSColorValue = SiteTheme.white._900,
    contentAlignment: Alignment = Alignment.TopStart,
    content: @Composable ColumnScope.() -> Unit,
) {
    val context = rememberPageContext()
    var scrollY by remember { mutableStateOf(0.0) }
    val isScrolled = scrollY > 20

    LaunchedEffect(title) {
        document.title = "${StringResources.APP_NAME} - $title"
    }

    LaunchedEffect(description) {
        description?.let { desc ->
            // Update or create meta description tag
            document.updateHeadTag(
                selector = "meta[name='description']",
                tagName = "meta",
                attributes = mapOf(
                    "name" to "description",
                    "content" to desc
                )
            )

            // Add Open Graph description
            document.updateHeadTag(
                selector = "meta[property='og:description']",
                tagName = "meta",
                attributes = mapOf(
                    "property" to "og:description",
                    "content" to desc
                )
            )
        }
    }

    LaunchedEffect(title, ogImage) {
        // Open Graph title
        document.updateHeadTag(
            selector = "meta[property='og:title']",
            tagName = "meta",
            attributes = mapOf(
                "property" to "og:title",
                "content" to "${StringResources.APP_NAME} - $title"
            )
        )

        // Open Graph type
        document.updateHeadTag(
            selector = "meta[property='og:type']",
            tagName = "meta",
            attributes = mapOf(
                "property" to "og:type",
                "content" to "website"
            )
        )

        // Open Graph URL
        document.updateHeadTag(
            selector = "meta[property='og:url']",
            tagName = "meta",
            attributes = mapOf(
                "property" to "og:url",
                "content" to "${UrlResources.SITE_BASE_URL}${context.route.path}"
            )
        )

        // Open Graph locale
        document.updateHeadTag(
            selector = "meta[property='og:locale']",
            tagName = "meta",
            attributes = mapOf(
                "property" to "og:locale",
                "content" to "pl_PL"
            )
        )

        // Open Graph image (if provided)
        ogImage?.let { image ->
            document.updateHeadTag(
                selector = "meta[property='og:image']",
                tagName = "meta",
                attributes = mapOf(
                    "property" to "og:image",
                    "content" to "${UrlResources.SITE_BASE_URL}$image"
                )
            )
        }

        // Canonical URL
        document.updateHeadTag(
            selector = "link[rel='canonical']",
            tagName = "link",
            attributes = mapOf(
                "rel" to "canonical",
                "href" to "${UrlResources.SITE_BASE_URL}${context.route.path}"
            )
        )
    }

    LaunchedEffect(Unit) {
        window.addEventListener("scroll", { scrollY = window.scrollY })
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .minHeight(100.percent)
            .backgroundColor(backgroundColor)
            .gridTemplateRows { size(1.fr); size(minContent) },
        contentAlignment = contentAlignment,
    ) {
        Column(
            modifier = Modifier.fillMaxSize().gridRow(1).id("main-content"),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {
            SkipLink()
            NavHeader(
                context = context,
                modifier = Modifier
                    .position(Position.Fixed)
                    .top(0.px)
                    .backgroundColor(SiteTheme.white._900)
                    .thenIf(
                        condition = isScrolled,
                        other = Modifier.boxShadow(
                            boxShadow = BoxShadow.of(
                                offsetX = 0.px,
                                offsetY = 0.px,
                                blurRadius = 6.px,
                                spreadRadius = 0.px,
                                color = SiteTheme.black._900.toRgb().copy(alpha = 30)
                            )
                        )
                    )
                    .transition(Transition.of("background-color", 0.1.s))
                    .zIndex(1001),
            )
            content()
        }
        Footer(Modifier.fillMaxWidth().gridRow(2))
    }
}
